import { HydrationBoundary, useQueries, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import {
  getBrokerDetails,
  getEventSeriesTicketDetails,
  getEventTicketDetails,
} from '@apiCalls';
import { CheckoutWrapper } from '@components';
import { DateSelector } from '@components/EventSelect/DateSelector';
import { ListSelector } from '@components/EventSelect/ListSelector';
import { DATEPICKERTYPE } from '@constants';
import { initialEventState, useAppContext } from '@contexts';
import { loadServerSideData } from '@utils/loadServerSideData';
import tracker, { InitTrackingParameters } from '@utils/tracking';

import { TicketListBlock } from '.';

const DateSelect = ({
  events: eventsProperty,
  eventSeries: eventSeriesProperty,
}: {
  eventSeries?: IEventSeries;
  events?: IEvent[];
}) => {
  const {
    push,
    query: { repId },
  } = useRouter();
  const {
    event,
    setEvent,
    eventSeries,
    setEventSeries,
    ticketDetails: { tickets: selectedTickets },
  } = useAppContext();
  const repIdParameter = repId ? `?repId=${repId}` : '';
  const ticketQueries: {
    queryKey: string[];
    queryFn: () => Promise<ITicket[] | ITicketSummary[] | undefined>;
    enabled?: boolean;
  }[] = [];
  if (eventSeriesProperty && eventSeriesProperty.ID > 0) {
    ticketQueries.push({
      queryKey: ['tickets-series', String(eventSeriesProperty.ID)],
      queryFn: () => getEventSeriesTicketDetails(eventSeriesProperty.ID),
      enabled: eventSeriesProperty.ID > 0,
    });
  }
  if (event && event.ID > 0) {
    ticketQueries.push({
      queryKey: ['tickets', String(event.ID)],
      queryFn: () => getEventTicketDetails(event.ID),
      enabled: event.ID > 0,
    });
  }
  const ticketsData = useQueries({ queries: ticketQueries });
  const ticketsAreLoading = ticketsData.some(
    query => query.status === 'pending'
  );
  const ticketsReturnedError = ticketsData.some(
    query => query.status === 'error'
  );
  const tickets = ticketsData.flatMap(query => query.data).filter(Boolean) as
    | ITicket[]
    | ITicketSummary[];
  const { data: broker } = useQuery({
    queryKey: ['broker', event.ID],
    queryFn: () => getBrokerDetails(event),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  });
  const [events, setEvents] = useState<IEvent[]>();
  const [isDateSelected, setIsDateSelected] = useState(false);
  const [isTicketListBlockSuccess, setIsTicketListBlockSuccess] =
    useState(true);

  const handleContinue = () => {
    tracker.track({
      event: {
        event: 'Date Selected',
        category: 'Date Selection',
        value: eventSeries?.ID ?? event.ID,
      },
      platforms: ['ga'],
    });
    if (tickets?.some(ticket => ticket.HasTimeslot)) {
      push(`/event/${event.ID}/checkout/timeslot-select${repIdParameter}`);
    } else if (isDateSelected) {
      push(`/event/${event.ID}/checkout${repIdParameter}`);
    } else if (eventSeries && selectedTickets && selectedTickets.length > 0) {
      push(`/event/${eventSeries.ID}/checkout/billing${repIdParameter}`);
    }
  };

  useEffect(() => {
    if (eventSeriesProperty) {
      setEventSeries(eventSeriesProperty);
    }
    if (eventsProperty && eventsProperty.length > 0) {
      setEvents(eventsProperty);
    }
    if (event.ID) {
      setEvent(initialEventState());
    }
  }, [eventSeriesProperty]);

  useEffect(() => {
    const configs: InitTrackingParameters[] = [
      {
        level: 'platform',
        trackingIds: {
          TrackingGoogleID:
            process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TRACKING_ID,
          TrackingMetaID: process.env.NEXT_PUBLIC_META_TRACKING_ID,
        },
      },
    ];
    if (event.ID !== 0 && event.Organisation) {
      configs.push({
        level: 'organisation',
        trackingIds: {
          TrackingGoogleID: event.Organisation.TrackingGoogleID,
          TrackingMetaID: event.Organisation.TrackingMetaID,
        },
      });
    }
    if (broker) {
      configs.push({
        level: 'broker',
        trackingIds: {
          TrackingGoogleID: broker.TrackingGoogleID,
          TrackingMetaID: broker.TrackingMetaID,
        },
      });
    }
    tracker.init(configs);
  }, [event, broker]);

  // FIXME causes initial black flash
  if (!eventSeries) {
    return <></>;
  }

  return (
    <CheckoutWrapper
      blocked={
        (!isDateSelected && selectedTickets?.length === 0) ||
        ticketsAreLoading ||
        ticketsReturnedError ||
        !isTicketListBlockSuccess
      }
      buttonText={isDateSelected ? 'Select Date' : 'Continue'}
      onButtonClick={handleContinue}
    >
      <DateSelectBlock
        events={events}
        eventSeries={eventSeries}
        onSelected={setIsDateSelected}
        onTicketListStatusChanged={setIsTicketListBlockSuccess}
      />
    </CheckoutWrapper>
  );
};

interface DateSelectBlockProperties {
  events?: IEvent[];
  eventSeries?: IEventSeries;
  onSelected: (isSelected: boolean) => void;
  onTicketListStatusChanged: (isSuccess: boolean) => void;
}

export const DateSelectBlock = ({
  events,
  eventSeries,
  onSelected,
  onTicketListStatusChanged: onStatusChanged,
}: DateSelectBlockProperties) => {
  const { setEvent } = useAppContext();
  const handleEventDateSelect = (date: Date) => {
    if (!events) {
      return;
    }
    const selectedEvent = getEventByDate(events, date);
    if (selectedEvent) {
      setEvent(selectedEvent);
      onSelected(true);
    }
  };
  const handleEventListSelect = (event: IEvent) => {
    if (!events) {
      return;
    }
    setEvent(event);
    onSelected(true);
  };

  useEffect(() => {
    // initial set success to false
    if (eventSeries?.IsJointEvent) {
      onStatusChanged(false);
    }
  }, [eventSeries, onStatusChanged]);

  return (
    <>
      {eventSeries?.IsJointEvent && (
        <div style={{ marginBottom: '20px' }}>
          <TicketListBlock
            title='Series Tickets'
            description='These tickets grant access to every day of this event.'
            onStatusChange={onStatusChanged}
            queryKeyID={eventSeries.ID}
            queryFn={() => getEventSeriesTicketDetails(eventSeries.ID)}
          />
        </div>
      )}
      <div className='appCheckoutStepHeading'>
        <h1 className='appCheckoutStepTitle'>Select Date</h1>
        <p className='appCheckoutStepDescription'>
          This event occurs on multiple days.
        </p>
      </div>
      {events &&
        eventSeries &&
        (eventSeries.DatePickerType === DATEPICKERTYPE.Calendar ? (
          <DateSelector
            events={events}
            onEventDateSelect={handleEventDateSelect}
          />
        ) : (
          <ListSelector events={events} onEventSelect={handleEventListSelect} />
        ))}
      {(!events || events.length === 0) && (
        <div className='appCheckoutStepHeading' style={{ height: '100%' }}>
          <p className='error-message appCheckoutStepDescription'>
            No events found for the event series.
          </p>
        </div>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context =>
  loadServerSideData(context);

export default function DateSelectPageRoute({
  dehydratedState,
  events,
  eventSeries,
}: {
  events?: IEvent[];
  eventSeries?: IEventSeries;
  dehydratedState: any;
}) {
  return (
    <HydrationBoundary state={dehydratedState}>
      <DateSelect events={events} eventSeries={eventSeries} />
    </HydrationBoundary>
  );
}

/**
 * NOTE: function assumes that there's only one event for the given date, otherwise list selector is used.
 **/
export const getEventByDate = (events: IEvent[], date: Date) => {
  const selectedDate = dayjs(date);
  return events.find(event => dayjs(event.Date).isSame(selectedDate, 'day'));
};
